/**
 * Admin Levels - Sistema de permissões baseado em níveis
 *
 * Níveis de acesso administrativo que determinam quais funcionalidades
 * e recursos cada usuário pode acessar no sistema.
 */

export const ADMIN_LEVELS = {
  DISABLED: 0,        // Login desabilitado
  BASIC: 1,           // Acesso básico ao Portal Admin
  SUPER: 2,           // Portal Admin + SOS Portal
  API: 3,             // Portal Admin + SOS Portal + API
  MASTER: 4,          // Acesso completo
  LEGENDARY: 5,       // Acesso completo + privilégios extras
  GOD: 99             // Acesso total + funcionalidades especiais
};

/**
 * Mapeamento de strings do backend para níveis numéricos
 * Backend retorna: "Default", "SuperAdmin", "ApiAdmin", "MasterAdmin", "LegendaryAdmin", "GodAdmin"
 */
export const ADMIN_LEVEL_FROM_STRING = {
  'Disabled': ADMIN_LEVELS.DISABLED,
  'Default': ADMIN_LEVELS.BASIC,
  // O auth-server emite enum.ToString(); o valor 1 do enum é 'BasicAdmin'
  // (não 'Basic'). Sem esta chave explícita, o gate de hierarquia (GodAdmin)
  // dependeria do fallback silencioso a BASIC(1) — frágil se o backend mudar.
  'BasicAdmin': ADMIN_LEVELS.BASIC,
  'SuperAdmin': ADMIN_LEVELS.SUPER,
  'ApiAdmin': ADMIN_LEVELS.API,
  'MasterAdmin': ADMIN_LEVELS.MASTER,
  'LegendaryAdmin': ADMIN_LEVELS.LEGENDARY,
  'GodAdmin': ADMIN_LEVELS.GOD
};

/**
 * Mapeamento de níveis numéricos para strings do backend (para PUT/POST)
 */
export const ADMIN_LEVEL_TO_STRING = {
  [ADMIN_LEVELS.DISABLED]: 'Disabled',
  [ADMIN_LEVELS.BASIC]: 'Default',
  [ADMIN_LEVELS.SUPER]: 'SuperAdmin',
  [ADMIN_LEVELS.API]: 'ApiAdmin',
  [ADMIN_LEVELS.MASTER]: 'MasterAdmin',
  [ADMIN_LEVELS.LEGENDARY]: 'LegendaryAdmin',
  [ADMIN_LEVELS.GOD]: 'GodAdmin'
};

/**
 * Converte admin_level do backend (string) para número
 * @param {string|number} backendLevel - "Default", "GodAdmin", etc ou número
 * @returns {number}
 */
export function adminLevelFromBackend(backendLevel) {
  if (typeof backendLevel === 'number') return backendLevel;
  return ADMIN_LEVEL_FROM_STRING[backendLevel] ?? ADMIN_LEVELS.BASIC;
}

/**
 * Converte admin_level do frontend (número) para string do backend
 * @param {number} level - 1, 2, 3, etc
 * @returns {string}
 */
export function adminLevelToBackend(level) {
  return ADMIN_LEVEL_TO_STRING[level] ?? 'Default';
}

/**
 * Nomes amigáveis para cada nível de admin
 */
export const ADMIN_LEVEL_NAMES = {
  [ADMIN_LEVELS.DISABLED]: 'Desabilitado',
  [ADMIN_LEVELS.BASIC]: 'Administrador Básico',
  [ADMIN_LEVELS.SUPER]: 'Super Administrador',
  [ADMIN_LEVELS.API]: 'Administrador API',
  [ADMIN_LEVELS.MASTER]: 'Master Admin',
  [ADMIN_LEVELS.LEGENDARY]: 'Legendary Admin',
  [ADMIN_LEVELS.GOD]: 'God Mode'
};

/**
 * Descrições detalhadas de cada nível
 */
export const ADMIN_LEVEL_DESCRIPTIONS = {
  [ADMIN_LEVELS.DISABLED]: 'Login desabilitado - sem acesso ao sistema',
  [ADMIN_LEVELS.BASIC]: 'Acesso básico ao Saúde Digital Admin Portal',
  [ADMIN_LEVELS.SUPER]: 'Acesso ao Saúde Digital Admin Portal e SOS Portal',
  [ADMIN_LEVELS.API]: 'Acesso ao Admin Portal, SOS Portal e APIs',
  [ADMIN_LEVELS.MASTER]: 'Acesso completo a todas as funcionalidades',
  [ADMIN_LEVELS.LEGENDARY]: 'Acesso completo com privilégios administrativos extras',
  [ADMIN_LEVELS.GOD]: 'Acesso total incluindo funcionalidades God Mode'
};

/**
 * Badges de cores para cada nível (para UI)
 */
export const ADMIN_LEVEL_BADGES = {
  [ADMIN_LEVELS.DISABLED]: { color: '#6c757d', icon: 'fas fa-ban' },
  [ADMIN_LEVELS.BASIC]: { color: '#17a2b8', icon: 'fas fa-user' },
  [ADMIN_LEVELS.SUPER]: { color: '#007bff', icon: 'fas fa-user-shield' },
  [ADMIN_LEVELS.API]: { color: '#6f42c1', icon: 'fas fa-code' },
  [ADMIN_LEVELS.MASTER]: { color: '#fd7e14', icon: 'fas fa-crown' },
  [ADMIN_LEVELS.LEGENDARY]: { color: '#e83e8c', icon: 'fas fa-gem' },
  [ADMIN_LEVELS.GOD]: { color: '#ffc107', icon: 'fas fa-bolt' }
};

/**
 * Verifica se um nível tem acesso mínimo requerido
 * @param {number} userLevel - Nível do usuário
 * @param {number} requiredLevel - Nível mínimo requerido
 * @returns {boolean}
 */
export function hasMinimumLevel(userLevel, requiredLevel) {
  return userLevel >= requiredLevel;
}

/**
 * Verifica se um nível está em uma lista de níveis permitidos
 * @param {number} userLevel - Nível do usuário
 * @param {number[]} allowedLevels - Lista de níveis permitidos
 * @returns {boolean}
 */
export function hasAnyOfLevels(userLevel, allowedLevels = []) {
  return allowedLevels.includes(userLevel);
}
